import { useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { HiX, HiShieldCheck, HiLightningBolt } from "react-icons/hi";
import { IoCheckmarkSharp } from "react-icons/io5";
import { FiInfo } from "react-icons/fi";

import { AnimatedTooltip } from "MVVM/Components/AnimatedTooltip";
import { ClipQualityIcon } from "MVVM/Components/Icons";
import { useUser } from "MVVM/Hooks/useUser";
import {
  useClipQualitySettingsModal,
  useCs2HudSettingsModal,
} from "MVVM/Hooks/useClipQualitySettingsModal";
import {
  useClipQualityConfigs,
  ClipQualityConfigSettings,
} from "MVVM/Hooks/useClipQualityConfigs";
import { NewPill } from "MVVM/Components/ClipQualitySettings/ClipQualityBubble";
import cs2Logo from "MVVM/Assets/GameLogos/cs2.svg";
import { analytics } from "MVVM/Analytics";
import { usePortal } from "MVVM/Hooks/usePortal";
import { SignatureSelectorModal } from "MVVM/Components/Signatures";
import { useAutoSave } from "./useAutoSave";
import { useAutoClip } from "./useAutoClip";

const AUTO_SAVE_FILTERS = [
  {
    id: "all",
    label: "Just save everything",
    description: "Every clip you create will be saved automatically",
  },
  {
    id: "aces",
    label: "Aces only",
    description: "Only auto-save your ace rounds",
  },
  {
    id: "4k",
    label: "4Ks and above",
    description: "Auto-save your 4Ks and aces",
  },
  {
    id: "3k",
    label: "3Ks and above",
    description: "Auto-save your 3Ks, 4Ks, and aces",
  },
];

const AUTOCLIP_PRESETS = [
  {
    threshold: 30,
    label: "Everything",
    description: "Clip every play worth watching",
  },
  {
    threshold: 50,
    label: "Solid plays",
    description: "Multi-kills, clutches, and better",
  },
  {
    threshold: 90,
    label: "Highlights only",
    description: "Just your very best moments",
  },
];

interface ClipSettingsModalProps {
  onClose: () => void;
}

const describeQualityConfig = (
  settings: ClipQualityConfigSettings,
  blurLabelMap: Record<number, string>,
): string => {
  const { resolution, framerate, frameBlendBlur } = settings;
  let text = `Your clips render at ${resolution} ${framerate}fps`;
  if (frameBlendBlur > 1) {
    const blurLabel = blurLabelMap[frameBlendBlur] ?? `${frameBlendBlur}x`;
    text += ` ${blurLabel.toLowerCase()} blur`;
  }
  return text;
};

export const ClipSettingsModal = ({ onClose }: ClipSettingsModalProps) => {
  const { allstarUser } = useUser();
  const { open: openClipQuality } = useClipQualitySettingsModal();
  const { open: openCs2Hud } = useCs2HudSettingsModal();
  const { configs, currentConfig, blurLabelMap } = useClipQualityConfigs();

  const currentConfigObj = currentConfig
    ? configs.find((c) => c.configName === currentConfig)
    : undefined;
  const {
    optimisticValue,
    loading,
    isEligible,
    handleToggle,
    upsellOpen,
    UpsellPortal,
  } = useAutoSave();
  const {
    enabled: autoClipEnabled,
    threshold: autoClipThreshold,
    loading: autoClipLoading,
    isEligible: autoClipEligible,
    handleToggle: handleAutoClipToggle,
    handleThresholdSelect,
    upsellOpen: autoClipUpsellOpen,
    UpsellPortal: AutoClipUpsellPortal,
  } = useAutoClip();

  const avatar = allstarUser?.user?.avatar;
  const username = allstarUser?.user?.username;

  const {
    portalOpen: signatureSelectorOpen,
    setPortalOpen: setSignatureSelectorOpen,
    Portal: SignatureSelectorPortal,
  } = usePortal(
    <SignatureSelectorModal
      onSave={() => setSignatureSelectorOpen(false)}
      onCloseClick={() => setSignatureSelectorOpen(false)}
    />,
  );

  const handleOpenQuality = () => {
    onClose();
    setTimeout(() => openClipQuality(), 150);
  };

  const handleOpenHud = () => {
    onClose();
    setTimeout(() => openCs2Hud(), 150);
  };

  const handleOpenSignature = () => {
    analytics.track("Signature - Open Modal", {
      where: "Clip Settings",
      type: "Manage",
    });
    setSignatureSelectorOpen(true);
  };

  const [clickedFilter, setClickedFilter] = useState<string | null>(null);

  const handleFilterClick = (filterId: string) => {
    if (filterId === "all") return;
    analytics.track("Auto-Save Filter - Clicked", {
      filter: filterId,
      source: "clip-settings",
    });
    setClickedFilter(filterId);
  };

  return (
    <>
      {createPortal(
        <Overlay onClick={onClose}>
          <Modal onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={onClose}>
              <HiX size={18} />
            </CloseButton>

            <HeaderRow>
              {avatar && <Avatar src={avatar} alt={username || ""} />}
              <HeaderText>
                <Title>Clipping Settings</Title>
                <Subtitle>Manage your clipping settings</Subtitle>
              </HeaderText>
            </HeaderRow>

            <CardGrid>
              <Card onClick={handleOpenHud}>
                <CardIcon>
                  <GameLogo src={cs2Logo} alt="" />
                </CardIcon>
                <CardTitle>
                  HUD &amp; Crosshair
                  <NewPill>New</NewPill>
                </CardTitle>
                <CardDescription>
                  Choose which HUD elements and crosshair show on your
                  Counter-Strike clips
                </CardDescription>
              </Card>

              <Card onClick={handleOpenQuality}>
                <CardIcon>
                  <ClipQualityIcon size={20} />
                </CardIcon>
                <CardTitle>Clip Quality</CardTitle>
                <CardDescription>
                  {currentConfigObj
                    ? describeQualityConfig(
                        currentConfigObj.settings,
                        blurLabelMap,
                      )
                    : "Set the resolution and framerate your clips render at"}
                </CardDescription>
              </Card>

              <Card onClick={handleOpenSignature}>
                <CardIcon>
                  <SignatureIcon
                    src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/static/loadouts/sig.svg`}
                    alt=""
                  />
                </CardIcon>
                <CardTitle>My Signature</CardTitle>
                <CardDescription>
                  Choose the cards and music that play on your clips
                </CardDescription>
              </Card>
            </CardGrid>

            <SettingsList>
              <SectionHeader>
                <HiLightningBolt size={14} />
                Autocapture
              </SectionHeader>

              <SettingRow>
                <SettingInfo>
                  <SettingLabel>
                    Autocapture my matches
                    <AnimatedTooltip
                      content="After each match ends, Allstar automatically clips your best plays — no clipping required"
                      position="right"
                      maxWidth={220}
                    >
                      <InfoIconWrap>
                        <FiInfo size={13} />
                      </InfoIconWrap>
                    </AnimatedTooltip>
                  </SettingLabel>
                  <SettingDescription>
                    Get your best moments clipped automatically after every
                    match
                  </SettingDescription>
                </SettingInfo>
                <Checkbox
                  $checked={autoClipEnabled}
                  $loading={autoClipLoading}
                  onClick={handleAutoClipToggle}
                >
                  {autoClipEnabled && <IoCheckmarkSharp size={12} />}
                </Checkbox>
              </SettingRow>

              {autoClipEnabled && autoClipEligible && (
                <FilterGroup>
                  {AUTOCLIP_PRESETS.map((preset) => (
                    <FilterOption
                      key={preset.threshold}
                      $selected={preset.threshold === autoClipThreshold}
                      onClick={() => handleThresholdSelect(preset.threshold)}
                    >
                      <RadioDot
                        $selected={preset.threshold === autoClipThreshold}
                      />
                      <FilterInfo>
                        <FilterLabel>{preset.label}</FilterLabel>
                        <FilterDescription>
                          {preset.description}
                        </FilterDescription>
                      </FilterInfo>
                    </FilterOption>
                  ))}
                </FilterGroup>
              )}

              <SectionHeader>
                <HiShieldCheck size={14} />
                Auto-save
              </SectionHeader>

              <SettingRow>
                <SettingInfo>
                  <SettingLabel>
                    Auto-save Clips
                    <AnimatedTooltip
                      content="When enabled, your new clips are saved automatically so they won't expire after 72 hours"
                      position="right"
                      maxWidth={220}
                    >
                      <InfoIconWrap>
                        <FiInfo size={13} />
                      </InfoIconWrap>
                    </AnimatedTooltip>
                  </SettingLabel>
                  <SettingDescription>
                    Keep every clip you make instead of losing it after 72 hours
                  </SettingDescription>
                </SettingInfo>
                <Checkbox
                  $checked={optimisticValue}
                  $loading={loading}
                  onClick={handleToggle}
                >
                  {optimisticValue && <IoCheckmarkSharp size={12} />}
                </Checkbox>
              </SettingRow>

              {optimisticValue && isEligible && (
                <FilterGroup>
                  {AUTO_SAVE_FILTERS.map((filter) => (
                    <FilterOption
                      key={filter.id}
                      $selected={filter.id === "all"}
                      onClick={() => handleFilterClick(filter.id)}
                    >
                      <RadioDot $selected={filter.id === "all"} />
                      <FilterInfo>
                        <FilterLabel>{filter.label}</FilterLabel>
                        {clickedFilter === filter.id ? (
                          <FilterFeedback>
                            We're still working on this, we'll let you know when
                            this is available!
                          </FilterFeedback>
                        ) : (
                          <FilterDescription>
                            {filter.description}
                          </FilterDescription>
                        )}
                      </FilterInfo>
                    </FilterOption>
                  ))}
                </FilterGroup>
              )}
            </SettingsList>
          </Modal>
        </Overlay>,
        document.getElementById("root") || document.body,
      )}
      {upsellOpen && UpsellPortal}
      {autoClipUpsellOpen && AutoClipUpsellPortal}
      {signatureSelectorOpen && SignatureSelectorPortal}
    </>
  );
};

const Overlay = styled(Flex)`
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
  bottom: 0;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 101;
`;

const Modal = styled(Flex)`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  border-radius: 12px;
  flex-direction: column;
  max-width: 720px;
  padding: 28px;
  position: relative;
  width: 90vw;
`;

const CloseButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 4px;
  position: absolute;
  right: 16px;
  top: 16px;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha20};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const HeaderRow = styled(Flex)`
  align-items: center;
  gap: 14px;
`;

const Avatar = styled.img`
  border-radius: 50%;
  flex-shrink: 0;
  height: 40px;
  object-fit: cover;
  width: 40px;
`;

const HeaderText = styled(Flex)`
  flex-direction: column;
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 24px;
  font-weight: 700;
`;

const Subtitle = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 15px;
  margin-top: 4px;
`;

const CardGrid = styled(Box)`
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  margin-top: 24px;
`;

const Card = styled.button`
  align-items: flex-start;
  background: ${({ theme }) => theme.colors.navy};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha10};
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 18px 16px;
  text-align: left;
  transition:
    border-color 0.15s ease,
    background 0.15s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.navy50};
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const CardIcon = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  height: 24px;
  justify-content: center;
`;

const CardTitle = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 17px;
  font-weight: 600;
  gap: 8px;
`;

const CardDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 14px;
  line-height: 1.4;
`;

const SettingsList = styled(Flex)`
  flex-direction: column;
  margin-top: 20px;
`;

const SectionHeader = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 12px;
  font-weight: 600;
  gap: 6px;
  letter-spacing: 0.8px;
  margin-bottom: 4px;
  margin-top: 4px;
  text-transform: uppercase;
`;

const GameLogo = styled.img`
  height: 20px;
  width: 20px;
`;

const SignatureIcon = styled.img`
  height: 19px;
  width: 19px;
`;

const SettingRow = styled(Flex)`
  align-items: center;
  gap: 16px;
  justify-content: space-between;
  padding: 12px 0;
  text-align: left;
  width: 100%;
`;

const SettingInfo = styled(Flex)`
  flex-direction: column;
  gap: 2px;
`;

const SettingLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 17px;
  font-weight: 600;
  gap: 6px;
`;

const SettingDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 14px;
`;

const InfoIconWrap = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: help;
  opacity: 0.6;
  transition: opacity 0.15s;

  &:hover {
    opacity: 1;
  }
`;

const Checkbox = styled.div<{ $checked: boolean; $loading?: boolean }>`
  align-items: center;
  background-color: ${({ $checked, theme }) =>
    $checked ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $checked, theme }) =>
      $checked ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 4px;
  color: white;
  cursor: pointer;
  display: flex;
  flex-shrink: 0;
  height: 20px;
  justify-content: center;
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  transition: all 150ms ease;
  width: 20px;

  &:hover {
    border-color: ${({ theme }) => theme.colors.envy};
  }
`;

const FilterGroup = styled(Flex)`
  flex-direction: column;
  gap: 2px;
  padding: 4px 0 8px 0;
`;

const FilterOption = styled(Flex)<{
  $selected?: boolean;
}>`
  align-items: flex-start;
  border-radius: 8px;
  cursor: pointer;
  gap: 10px;
  padding: 10px 12px;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => theme.colors.chalkAlpha05};
  }
`;

const RadioDot = styled.div<{ $selected?: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.envy : "transparent"};
  border: 2px solid
    ${({ $selected, theme }) =>
      $selected ? theme.colors.envy : theme.colors.chalkAlpha20};
  border-radius: 50%;
  flex-shrink: 0;
  height: 16px;
  margin-top: 2px;
  transition: all 150ms ease;
  width: 16px;
`;

const FilterInfo = styled(Flex)`
  flex-direction: column;
  gap: 1px;
`;

const FilterLabel = styled(Flex)`
  align-items: center;
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 15px;
  font-weight: 500;
  gap: 8px;
`;

const FilterDescription = styled(Text)`
  color: ${({ theme }) => theme.colors.chalk};
  font-family: Barlow;
  font-size: 13px;
`;

const FilterFeedback = styled(Text)`
  color: ${({ theme }) => theme.colors.envy};
  font-family: Barlow;
  font-size: 13px;
  font-weight: 500;
`;
